import type React from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Bell,
  X,
  Check,
  Filter,
  Clock,
  AlertTriangle,
  Info,
  CheckCircle,
  RefreshCw,
} from "lucide-react";
import { useNotifications } from "../../hooks/useNotifications";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { useModules } from "../../hooks/useModules";
import { clearCurrentShipment } from "../../store/slices/shipmentSlice";
import { toast } from "react-toastify";

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSettings: () => void;
  onShipmentClick?: (shipmentId: string) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  onClose,
  // onOpenSettings,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("notifications");
  const [filter, setFilter] = useState<"all" | "unread">("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingShipment, setIsLoadingShipment] = useState(false);

  const {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    getNotifications,
    getUnreadNotifications,
    clearNotificationError,
  } = useNotifications();

  const dispatch = useAppDispatch();
  const { has } = useModules();
  const { loading: shipmentLoading } = useAppSelector(
    (state) => state.shipment,
  );

  useEffect(() => {
    if (isOpen) {
      if (filter === "unread") {
        getUnreadNotifications();
      } else {
        getNotifications();
      }
    }
  }, [isOpen, filter, getNotifications, getUnreadNotifications]);

  useEffect(() => {
    return () => {
      if (error) {
        clearNotificationError();
      }
    };
  }, [error, clearNotificationError]);

  useEffect(() => {
    if (!isOpen) {
      clearCurrentShipment();
    }
  }, [isOpen, dispatch]);

  const filteredNotifications = notifications.filter(
    (notification) => filter === "all" || !notification.isRead,
  );

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case "urgent":
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      case "high":
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case "medium":
        return <Info className="h-4 w-4 text-blue-500" />;
      case "low":
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case "status_update":
        return "bg-blue-100 text-blue-800";
      case "driver_comment":
        return "bg-purple-100 text-purple-800";
      case "user_onboarding":
        return "bg-green-100 text-green-800";
      case "task_assigned":
        return "bg-amber-100 text-amber-800";
      case "activity_reminder":
        return "bg-orange-100 text-orange-800";
      case "reclamation_filed":
        return "bg-red-100 text-red-800";
      case "poh_signed":
        return "bg-teal-100 text-teal-800";
      case "container_message":
        return "bg-indigo-100 text-indigo-800";
      case "container_geo_blocked":
        return "bg-rose-100 text-rose-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  // The badge normally just de-underscores the enum, which would render
  // "poh signed" — an acronym the transform can't know to keep uppercase.
  // The badge normally just de-underscores the enum. Two need saying properly:
  // "poh signed" hides an acronym the transform cannot know to keep uppercase,
  // and `replace` without a global flag only ever swaps the first underscore —
  // which left "container geo_blocked" on screen.
  const getTypeLabel = (type: string) =>
    type === "poh_signed" ? "handover signed" : type.replace(/_/g, " ");

  const formatTimestamp = (timestamp: string | Date) => {
    const date =
      typeof timestamp === "string" ? new Date(timestamp) : timestamp;
    const now = new Date();
    const diffInMinutes = Math.floor(
      (now.getTime() - date.getTime()) / (1000 * 60),
    );

    if (diffInMinutes < 1) return "Just now";
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;

    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;

    return date.toLocaleDateString();
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (filter === "unread") {
        await getUnreadNotifications();
      } else {
        await getNotifications();
      }
    } catch (error) {
      console.error("Error refreshing notifications:", error);
      toast.error(t("toast.refreshFailed"));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleFilterChange = (newFilter: "all" | "unread") => {
    setFilter(newFilter);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
      toast.success(t("toast.markAllReadSuccess"));
    } catch (error) {
      console.error("Error marking all as read:", error);
    }
  };

  const handleNotificationClick = async (notification: any) => {
    if (isLoadingShipment || shipmentLoading) {
      return;
    }

    if (
      notification.type === "task_assigned" ||
      notification.type === "activity_reminder"
    ) {
      if (!notification.relatedTask?._id) {
        toast.error(t("toast.noTaskAssociated"));
        return;
      }
      if (!notification.isRead) {
        markAsRead(notification._id);
      }
      onClose();
      navigate("/tasks");
      return;
    }

    /**
     * Container notifications open the container itself.
     *
     * Both of them are a driver asking for something only that page can give:
     * a step recorded for them, or an answer to a message. The container's page
     * carries the messages panel and the status control, so there is nowhere
     * else either of these could usefully land.
     *
     * The route needs the booking as well as the box, and the notification
     * carries it in its metadata rather than through a populate — an id is all
     * a link needs, and it survives the container being deleted afterwards.
     */
    if (
      notification.type === "container_message" ||
      notification.type === "container_geo_blocked"
    ) {
      const lineId = notification.relatedContainerLine;
      const orderId = notification.metadata?.containerOrderId;

      if (!lineId || !orderId) {
        toast.error(t("toast.noContainerAssociated"));
        return;
      }
      if (!notification.isRead) {
        markAsRead(notification._id);
      }
      onClose();
      navigate(`/container-orders/${orderId}/container/${lineId}`);
      return;
    }

    if (!notification.relatedShipment?._id) {
      toast.error(t("toast.noShipmentAssociated"));
      return;
    }

    // Reclamations open the shipments list with the reclamation side panel
    // already expanded for that shipment, rather than the shipment form.
    if (notification.type === "reclamation_filed") {
      if (!notification.isRead) {
        markAsRead(notification._id);
      }
      onClose();
      navigate("/shipments", {
        state: {
          openReclamationShipmentId: notification.relatedShipment._id,
          openReclamationShipmentNumber:
            notification.relatedShipment.shipmentNumber ??
            notification.metadata?.shipmentNumber,
          timestamp: Date.now(),
        },
      });
      return;
    }

    const shipmentId = notification.relatedShipment._id;
    setIsLoadingShipment(true);

    try {
      if (!notification.isRead) {
        markAsRead(notification._id);
      }
      onClose();
      navigate(`/shipments/${shipmentId}`);
    } catch (error) {
      console.error("Error loading shipment:", error);
      toast.error(t("toast.shipmentLoadFailed"));
    } finally {
      setIsLoadingShipment(false);
    }
  };

  /**
   * A notification is clickable only when the module it opens is one this
   * tenant still has.
   *
   * Notifications outlive the configuration that produced them — a shipment
   * notification stays in the list after shipments is switched off — so
   * without this check, clicking an old one would navigate into a route that
   * immediately redirects, which reads as the click having done nothing.
   */
  const isClickableNotification = (notification: any) => {
    const isTaskNotification =
      notification.type === "task_assigned" ||
      notification.type === "activity_reminder";

    if (isTaskNotification) {
      return Boolean(notification.relatedTask?._id) && has("tasks");
    }

    // A container notification needs both halves of its route and the module
    // that owns the page it opens — a tenant that has since switched container
    // orders off would otherwise land on a redirect that looks like a dead
    // click.
    if (
      notification.type === "container_message" ||
      notification.type === "container_geo_blocked"
    ) {
      return (
        Boolean(notification.relatedContainerLine) &&
        Boolean(notification.metadata?.containerOrderId) &&
        has("containerOrders")
      );
    }

    return Boolean(notification.relatedShipment?._id) && has("shipments");
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="absolute sm:right-0 right-[-25px] mt-2 sm:w-96 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <Bell className="h-5 w-5 text-dark-600" />
            <h3 className="font-semibold text-dark-900">{t("header.title")}</h3>
            {unreadCount !== null && unreadCount > 0 && (
              <span className="bg-[#C97A3C] text-white text-xs px-2 py-1 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing || loading}
              className="p-1 text-dark-500 hover:text-dark-700 transition-colors disabled:opacity-50"
              title={t("actions.refresh")}
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  isRefreshing || loading ? "animate-spin" : ""
                }`}
              />
            </button>
            <button
              onClick={onClose}
              className="p-1 text-dark-500 hover:text-dark-700 transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Filter and Actions */}
        <div className="flex items-center justify-between p-3 border-b border-gray-100">
          <div className="flex items-center space-x-2">
            <Filter className="h-4 w-4 text-dark-500" />
            <select
              value={filter}
              onChange={(e) =>
                handleFilterChange(e.target.value as "all" | "unread")
              }
              className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-primary focus:border-transparent"
              disabled={loading}
            >
              <option value="all">{t("filter.all")}</option>
              <option value="unread">{t("filter.unread")}</option>
            </select>
          </div>

          {unreadCount !== null && unreadCount > 0 && (
            <button
              onClick={handleMarkAllAsRead}
              disabled={loading}
              className="text-sm text-primary-600 hover:text-primary-700 transition-colors disabled:opacity-50"
            >
              {t("filter.markAllRead")}
            </button>
          )}
        </div>

        {error && (
          <div className="p-3 bg-red-50 border-b border-red-100">
            <p className="text-sm text-red-600">{error}</p>
            <button
              onClick={clearNotificationError}
              className="text-xs text-red-500 hover:text-red-700 mt-1"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Notifications List */}
        <div className="max-h-96 overflow-y-auto">
          {loading ? (
            <div className="p-8 text-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent mx-auto"></div>
              <p className="text-dark-500 mt-2">{t("loading")}</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell className="h-12 w-12 text-dark-300 mx-auto mb-3" />
              <p className="text-dark-500">
                {filter === "unread"
                  ? t("empty.noUnread")
                  : t("empty.noNotifications")}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filteredNotifications.map((notification) => {
                const isClickable = isClickableNotification(notification);

                return (
                  <div
                    key={notification._id}
                    className={`p-4 transition-colors ${
                      isClickable
                        ? "hover:bg-gray-50 cursor-pointer"
                        : "cursor-default"
                    } ${!notification.isRead ? "bg-blue-50/50" : ""} ${
                      isLoadingShipment && isClickable ? "opacity-50" : ""
                    }`}
                    onClick={() =>
                      isClickable && handleNotificationClick(notification)
                    }
                  >
                    <div className="flex items-start space-x-3">
                      <div className="shrink-0 mt-1">
                        {getPriorityIcon(notification.priority)}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4
                            className={`text-sm font-medium ${
                              !notification.isRead
                                ? "text-dark-900"
                                : "text-dark-700"
                            }`}
                          >
                            {notification.title}
                          </h4>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-[#C97A3C] rounded-full shrink-0"></div>
                          )}
                        </div>

                        <p className="text-sm text-dark-600 mb-2">
                          {notification.message}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(
                                notification.type,
                              )}`}
                            >
                              {getTypeLabel(notification.type)}
                            </span>
                            <div className="flex items-center space-x-1 text-xs text-dark-500">
                              <Clock className="h-3 w-3" />
                              <span>
                                {formatTimestamp(notification.createdAt)}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center space-x-1">
                            {!notification.isRead && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  markAsRead(notification._id);
                                }}
                                className="p-1 text-dark-400 hover:text-green-600 transition-colors"
                                title={t("actions.markAsRead")}
                                disabled={loading}
                              >
                                <Check className="h-3 w-3" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationDropdown;
